import {
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import { FormSaveBar } from '@/components/FormSaveBar';
import { useCardForm } from '@/lib/useCardForm';
import {
  LEAD_MQL_STATUSES,
  LEAD_SQL_STATUSES,
  type Lead,
  type LeadKind,
  type LeadMqlStatus,
  type LeadSqlStatus,
  useUpdateLead,
} from '@/models/leads';

type Values = {
  reason: string;
  nextTaskAt: string | null;
  mqlStatus: LeadMqlStatus | null;
  sqlStatus: LeadSqlStatus | null;
  isSpam: boolean;
  kind: LeadKind;
  duplicateOfLeadId: number | null;
};

const dateOnly = (v: string | null | undefined) => (v ? v.slice(0, 10) : null);

export function FunnelCard({ lead }: { lead: Lead }) {
  const update = useUpdateLead();
  const { form, dirty, saving, handleSubmit } = useCardForm<Values>({
    initialValues: {
      reason: lead.reason ?? '',
      nextTaskAt: dateOnly(lead.nextTaskAt),
      mqlStatus: lead.mqlStatus ?? null,
      sqlStatus: lead.sqlStatus ?? null,
      isSpam: lead.isSpam ?? false,
      kind: lead.kind ?? 'client',
      duplicateOfLeadId: lead.duplicateOfLeadId ?? null,
    },
    save: ({ nextTaskAt, ...rest }) =>
      update.mutateAsync({
        id: lead.id,
        ...rest,
        ...(nextTaskAt === undefined
          ? {}
          : {
              nextTaskAt: nextTaskAt
                ? new Date(`${nextTaskAt}T09:00:00Z`).toISOString()
                : null,
            }),
      }),
    successMessage: 'Funnel saved',
  });

  const qualifiesNow =
    form.values.sqlStatus === 'Qualified' && lead.sqlStatus !== 'Qualified';

  return (
    <CollapsibleCard title="Funnel">
      <form onSubmit={handleSubmit}>
        <Stack gap="xs">
          <Group grow gap="xs">
            <Select
              label="MQL"
              size="xs"
              data={LEAD_MQL_STATUSES}
              clearable
              value={form.values.mqlStatus}
              onChange={(v) =>
                form.setFieldValue('mqlStatus', v as LeadMqlStatus | null)
              }
            />
            <Select
              label="SQL"
              size="xs"
              data={LEAD_SQL_STATUSES}
              clearable
              value={form.values.sqlStatus}
              onChange={(v) =>
                form.setFieldValue('sqlStatus', v as LeadSqlStatus | null)
              }
            />
          </Group>
          {qualifiesNow && (
            <Text size="xs" c="dimmed">
              Saving SQL = Qualified qualifies the request
              {lead.contactId == null
                ? ' and creates a contact and an account from the lead.'
                : '.'}
            </Text>
          )}
          <Group grow gap="xs">
            <TextInput
              label="Reason"
              size="xs"
              {...form.getInputProps('reason')}
            />
            <DateInput
              label="Next task"
              size="xs"
              valueFormat="DD.MM.YYYY"
              clearable
              value={form.values.nextTaskAt}
              onChange={(v) => form.setFieldValue('nextTaskAt', v)}
            />
          </Group>
          <Group grow gap="xs" align="flex-end">
            <Select
              label="Kind"
              size="xs"
              data={[
                { value: 'client', label: 'Client' },
                { value: 'agent', label: 'Agent / partner' },
              ]}
              value={form.values.kind}
              onChange={(v) => {
                if (v) form.setFieldValue('kind', v as LeadKind);
              }}
            />
            <NumberInput
              label="Duplicate of lead #"
              size="xs"
              min={1}
              allowDecimal={false}
              value={form.values.duplicateOfLeadId ?? ''}
              onChange={(v) =>
                form.setFieldValue(
                  'duplicateOfLeadId',
                  typeof v === 'number' ? v : null,
                )
              }
            />
          </Group>
          <Switch
            label="Spam"
            size="xs"
            checked={form.values.isSpam}
            onChange={(e) =>
              form.setFieldValue('isSpam', e.currentTarget.checked)
            }
          />
          {lead.qualifiedAt && (
            <Text size="xs" c="dimmed">
              Qualified {new Date(lead.qualifiedAt).toLocaleString()}
            </Text>
          )}
          {dirty && <FormSaveBar saving={saving} onDiscard={form.reset} />}
        </Stack>
      </form>
    </CollapsibleCard>
  );
}
